import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const fmtNum = (n) => {
  if (n == null || isNaN(n)) return '0';
  return n.toLocaleString('fr-FR');
};

const fmtDiamonds = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString('fr-FR');
};

const fmtDuration = (totalSeconds) => {
  if (!totalSeconds) return '0h 00min';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

const fmtWatchDuration = (seconds) => {
  if (!seconds) return '—';
  const s = Math.round(seconds);
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h${String(m).padStart(2, '0')}`;
  }
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m${String(sec).padStart(2, '0')}s`;
  }
  return `${s}s`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
};

const SessionsSection = ({ tiktokId, server, period }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tiktokId) return;
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const p = period || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
        const [pY, pM] = p.split('-').map(Number);
        const monthStart = new Date(Date.UTC(pY, pM - 1, 1)).toISOString();
        const monthEnd = new Date(Date.UTC(pY, pM, 1)).toISOString();

        const { data, error } = await supabase
          .from('creator_live_sessions')
          .select('room_id, start_time, end_time, duration_seconds, diamonds, viewers, pcu, acu, gifters, new_fans, impressions, avg_watch_duration_seconds')
          .eq('tiktok_id', tiktokId)
          .gte('start_time', monthStart)
          .lt('start_time', monthEnd)
          .order('start_time', { ascending: false });

        if (!error && data) setSessions(data);
      } catch (err) {
        console.error('Sessions fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [tiktokId, period]);

  const kpis = useMemo(() => {
    if (!sessions.length) return null;
    const count = sessions.length;
    const totalSeconds = sessions.reduce((s, r) => s + (r.duration_seconds || 0), 0);
    const avgSeconds = count > 0 ? totalSeconds / count : 0;
    const totalHours = totalSeconds / 3600;
    const totalDiamonds = sessions.reduce((s, r) => s + (r.diamonds || 0), 0);
    const diamondsPerHour = totalHours > 0 ? totalDiamonds / totalHours : 0;
    const totalViewers = sessions.reduce((s, r) => s + (r.viewers || 0), 0);
    const maxPcu = sessions.reduce((m, r) => Math.max(m, r.pcu || 0), 0);
    const weightedAcu = totalSeconds > 0
      ? sessions.reduce((s, r) => s + (r.acu || 0) * (r.duration_seconds || 0), 0) / totalSeconds
      : 0;
    const sessionsWithWatch = sessions.filter(r => r.avg_watch_duration_seconds > 0);
    const avgWatchDuration = sessionsWithWatch.length > 0
      ? sessionsWithWatch.reduce((s, r) => s + r.avg_watch_duration_seconds, 0) / sessionsWithWatch.length
      : 0;
    return { count, avgSeconds, diamondsPerHour, totalViewers, maxPcu, weightedAcu, avgWatchDuration };
  }, [sessions]);

  const last5 = sessions.slice(0, 5);

  const periodLabel = useMemo(() => {
    const p = period || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const [y, m] = p.split('-').map(Number);
    return `${t(`months.${m - 1}`)} ${y}`;
  }, [period, t]);

  const card = (children) => (
    <div style={{ background: '#111111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '14px' }}>
      {children}
    </div>
  );

  const label = (text) => (
    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', margin: '0 0 10px 0' }}>
      {text}
    </p>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ height: '80px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', animation: 'pulse 2s infinite' }} />
        ))}
      </div>
    );
  }

  if (!sessions.length) {
    return card(
      <>
        {label(t('admin.creators.sessionsLiveLabel', 'Sessions LIVE'))}
        <p style={{ textAlign: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', padding: '12px 0' }}>
          {t('admin.creators.noSessionThisMonth', 'Aucune session ce mois')}
        </p>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* KPIs row */}
      {card(
        <>
          {label(`${t('admin.creators.sessionsLiveLabel', 'Sessions LIVE')} — ${periodLabel}`)}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {[
              { l: t('admin.creators.sessionsCount', 'Sessions'), v: fmtNum(kpis?.count), c: '#3b82f6' },
              { l: t('admin.creators.avgDuration', 'Durée moy.'), v: fmtDuration(kpis?.avgSeconds), c: 'white' },
              { l: t('admin.creators.diamondsPerHour', 'Diamonds/h'), v: fmtDiamonds(Math.round(kpis?.diamondsPerHour || 0)), c: '#f97316' },
              { l: t('admin.creators.viewers', 'Viewers'), v: fmtDiamonds(kpis?.totalViewers), c: '#22c55e' },
              { l: t('admin.creators.pcuMax', 'PCU max'), v: fmtNum(kpis?.maxPcu), c: '#a855f7' },
              { l: t('admin.creators.acuAvg', 'ACU moy.'), v: fmtNum(Math.round(kpis?.weightedAcu || 0)), c: '#06b6d4' },
              { l: t('admin.creators.watchAvg', 'Watch moy.'), v: fmtWatchDuration(kpis?.avgWatchDuration), c: '#eab308' },
            ].map(({ l, v, c }) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>{l}</div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: c, fontFamily: 'Sora, sans-serif' }}>{v}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Table last 5 sessions */}
      {card(
        <>
          {label(t('admin.creators.last5Sessions', '5 dernières sessions'))}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>
              <thead>
                <tr>
                  {[
                    { key: 'date', label: t('admin.creators.colDate', 'Date') },
                    { key: 'duration', label: t('admin.creators.colDuration', 'Durée') },
                    { key: 'diamonds', label: t('admin.creators.colDiamonds', 'Diamonds') },
                    { key: 'viewers', label: t('admin.creators.colViewers', 'Viewers') },
                    { key: 'pcu', label: t('admin.creators.colPcu', 'PCU') },
                    { key: 'acu', label: t('admin.creators.colAcu', 'ACU') },
                    { key: 'watch', label: t('admin.creators.colWatch', 'Watch') },
                    { key: 'gifters', label: t('admin.creators.colGifters', 'Gifters') },
                  ].map(col => (
                    <th key={col.key} style={{
                      textAlign: col.key === 'date' || col.key === 'duration' ? 'left' : 'right',
                      padding: '6px 8px',
                      fontSize: '10px',
                      fontWeight: '500',
                      color: 'rgba(255,255,255,0.35)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      borderBottom: '1px solid #1f1f1f',
                    }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {last5.map((s, i) => (
                  <tr key={s.room_id || i} style={{ borderBottom: i < last5.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                      {fmtDate(s.start_time)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                      {fmtDuration(s.duration_seconds)}
                    </td>
                    <td style={{ padding: '8px', color: '#f97316', fontWeight: '500', textAlign: 'right' }}>
                      {fmtDiamonds(s.diamonds)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                      {fmtNum(s.viewers || 0)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                      {fmtNum(s.pcu || 0)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                      {fmtNum(s.acu || 0)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                      {fmtWatchDuration(s.avg_watch_duration_seconds)}
                    </td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                      {fmtNum(s.gifters || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionsSection;
